import type { TTaskStatus } from '@homepal/api-contract'
import React from 'react'

import { ETaskStatus } from 'types/GlobalTask'
import { TTask } from 'redux/hooks/Tasks'
import { SxProps } from '@mui/material'

import { useTaskActions } from 'redux/actions/Tasks'
import { useTranslation } from 'language/translator'

import Icon from 'components_new/atoms/Icon'
import LinearProgress from 'components_new/atoms/LinearProgress'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleTable, {
  SimpleTableProps
} from 'components_new/molecules/SimpleTable'
import ValueText from 'components_new/molecules/ValueText'

import SelectUser from 'components_new/organisms/SelectUser'
import TaskStatusChip from 'components_new/organisms/tasks/TaskStatusChip'

/**
 * One activity, stored or about to be created. A row without an `id` is a
 * preview and gets no link.
 */
export interface TActivityRow {
  id?: string
  noi_change: number | null
  responsible_account_id: string | null
  start_value: number | null
  status: TTaskStatus
  target_value: number | null
  title: string | null
}

interface TasksTableProps {
  /** Omitted for a read-only table. */
  onChangeOwner?: (item: TTask, accountId: string | null) => void
  items: TTask[]
  /** Omitted, the table grows with its rows. */
  maxHeight?: SimpleTableProps['maxHeight']
  preview?: boolean
  size?: 'small' | 'medium'
  stickyHeader?: boolean
  sx?: SxProps
}

const TasksTable = (props: TasksTableProps) => {
  const {
    items,
    maxHeight,
    onChangeOwner,
    preview,
    size = 'small',
    stickyHeader,
    sx
  } = props

  const { t, translateEnum } = useTranslation()
  const { tryUpdateTask } = useTaskActions()

  const hasSubtasks = items.some((item) => item.sub_tasks.length > 0)

  const handleUpdateStatus = (id: string | undefined, status: TTaskStatus) => {
    if (!id) return

    tryUpdateTask(id, { status })
  }

  const handleUpdateUser = (id: string | undefined, value: string | null) => {
    if (!id) return

    tryUpdateTask(id, {
      responsible_account_id: value
    })
  }

  return (
    <SimpleTable
      columns={[
        { title: preview ? undefined : t('tasks', 'task') },
        { title: preview ? undefined : t('org_units', 'unit') },
        { title: preview ? undefined : t('global', 'status') },
        ...(hasSubtasks
          ? [
              {
                align: 'left' as const,
                title: preview ? undefined : t('tasks', 'subtasks')
              }
            ]
          : []),
        {
          align: 'right',
          defaultSort: 'desc',
          title: preview ? undefined : t('analyses', 'potential')
        },
        {
          align: 'right',
          title: preview ? undefined : t('global', 'responsible')
        }
      ]}
      emptyPlaceholder={t('tasks', 'empty_title')}
      hover={true}
      maxHeight={maxHeight}
      rows={items.map((item) => ({
        cells: [
          <GenericListItem
            disablePadding={true}
            icon={{ name: 'Task' }}
            key="title"
            size={size}
            sx={{ maxWidth: { xs: 256, lg: 400 } }}
            title={item.title || t('global', 'unnamed')}
          />,
          <>{item.metric_filter_value}</>,
          <TaskStatusChip
            key="status"
            onChange={(status) => handleUpdateStatus(item.id, status)}
            size="xsmall"
            value={item.status}
          />,
          ...(hasSubtasks
            ? [
                <>
                  {item.sub_tasks.length > 0 ? (
                    <LinearProgress
                      customLabel={
                        <>
                          {`${
                            item.sub_tasks.filter(
                              (item) => item.status === ETaskStatus.DONE
                            ).length
                          }/${item.sub_tasks.length}`}{' '}
                          <Icon
                            name="Tasks"
                            sx={{ fontSize: '1em', verticalAlign: 'middle' }}
                          />
                        </>
                      }
                      value={
                        (item.sub_tasks.filter(
                          (item) => item.status === ETaskStatus.DONE
                        ).length /
                          item.sub_tasks.length) *
                        100
                      }
                      variant="determinate"
                    />
                  ) : null}
                </>
              ]
            : []),
          <ValueText
            align="right"
            color={item.noi_change === null ? undefined : 'success.text'}
            displayPositiveIndicator={true}
            key="change"
            unit="kr/år"
            value={item.noi_change}
          />,
          <SelectUser
            key="owner"
            onChange={(value) =>
              onChangeOwner
                ? onChangeOwner(item, value)
                : handleUpdateUser(item.id, value)
            }
            size="small"
            value={item.responsible_account_id}
          />
        ],
        href: item.id ? `/tasks/${item.id}` : undefined,
        id: item.id || item.metric_filter_value || undefined,
        sortValues: [
          { value: item.title },
          { value: item.metric_filter_value },
          { value: translateEnum('task_status', item.status).title },
          ...(hasSubtasks ? [{ disabled: true, value: null }] : []),
          { value: item.noi_change },
          { value: item.responsible_account_id }
        ]
      }))}
      size={size}
      stickyHeader={stickyHeader}
      sx={sx}
    />
  )
}

export default TasksTable
